export type SiteMode = 'CN' | 'GLOBAL';

/**
 * Paid listing checkout is OFF (CN company cannot take cards via Airwallex).
 * Free submit + admin review only until a proper receiving account exists.
 */
export const SUBMIT_PAYMENT_ENABLED = false;

/**
 * 页面展示公司对公账户：取得 ICP 经营许可证后再打开。
 * 关闭时不展示户名/开户行/账号。
 */
export const CN_BANK_TRANSFER_VISIBLE = false;

/**
 * SEO / 统计配置位（运行时）：在 public/index.html 的 window.__SITE_ANALYTICS__ 填写。
 * - baiduHmId: 百度统计 HM ID
 * - gaMeasurementId: GA4（G-xxxxxxxx）
 * - googleSiteVerification: Google Search Console HTML 验证码
 * 留空不加载脚本。下面常量仅作文档占位，真正生效看 index.html。
 */
export const BAIDU_HM_ID = '';
export const GA_MEASUREMENT_ID = '';
export const GOOGLE_SITE_VERIFICATION = '';

/** 国内站对公转账信息（证下来后配合 CN_BANK_TRANSFER_VISIBLE / SUBMIT_PAYMENT_ENABLED 使用） */
export type CnBankTransferInfo = {
  companyName: string;
  bankName: string;
  accountNo: string;
  transferNote: string;
};

export const CN_BANK_TRANSFER: CnBankTransferInfo = {
  companyName: '企新网（北京）科技有限公司',
  bankName: '【待填写 — 开户银行全称】',
  accountNo: '【待填写 — 对公银行账号】',
  transferNote: '转账备注请填写：工具名称 + 您的联系电话或邮箱，便于我们核对后优先审核。',
};

/** 双站对外统一联系邮箱 */
const PUBLIC_EMAIL = 'xinle@quanqiuai.com.sn';

/** 中文站对外电话（仅国内站展示） */
export const CN_CONTACT_PHONE = '010-89212028';

/** 企业微信二维码（public 静态资源） */
export const WECOM_QR_SRC = '/assets/wecom-qr.png';

export const GLOBAL_WHATSAPP_E164 = '85266545011';
export const GLOBAL_WHATSAPP_DISPLAY = '+852 6654 5011';
export const GLOBAL_WHATSAPP_URL = `https://wa.me/${GLOBAL_WHATSAPP_E164}`;

const SUPPORT_EMAIL: Record<SiteMode, string> = {
  CN: PUBLIC_EMAIL,
  GLOBAL: PUBLIC_EMAIL,
};

const CONTACT_EMAIL: Record<SiteMode, string> = {
  CN: PUBLIC_EMAIL,
  GLOBAL: PUBLIC_EMAIL,
};

export const supportEmail = (site?: SiteMode): string =>
  SUPPORT_EMAIL[site ?? detectSiteMode()];

export const contactEmail = (site?: SiteMode): string =>
  CONTACT_EMAIL[site ?? detectSiteMode()];

// Domain mapping: CN site ↔ GLOBAL site (different domains, not subdomains)
const DOMAIN_MAP: Record<string, { CN: string; GLOBAL: string }> = {
  'aihomehub.cn':     { CN: 'aihomehub.cn',     GLOBAL: 'globalaitool.com' },
  'globalaitool.com': { CN: 'aihomehub.cn',     GLOBAL: 'globalaitool.com' },
};

export const detectSiteMode = (host?: string): SiteMode => {
  const normalized = (host || window.location.host || '').toLowerCase().split(':')[0];
  if (normalized.startsWith('global.')) return 'GLOBAL';
  if (normalized.startsWith('cn.')) return 'CN';
  if (normalized === 'globalaitool.com' || normalized === 'www.globalaitool.com') return 'GLOBAL';
  if (normalized === 'aihomehub.cn' || normalized === 'www.aihomehub.cn') return 'CN';
  return 'CN';
};

export const defaultLanguageBySite = (site: SiteMode): 'zh-CN' | 'en-US' =>
  site === 'GLOBAL' ? 'en-US' : 'zh-CN';

export const buildTargetHost = (site: SiteMode, host?: string): string => {
  const current = (host || window.location.host || '').toLowerCase();
  const withPort = current.split(':');
  const domain = withPort[0].replace(/^(cn|global|www)\./, '');
  const port = withPort[1] ? `:${withPort[1]}` : '';

  // Check production domain mapping first
  const mapping = DOMAIN_MAP[domain];
  if (mapping) {
    return mapping[site];
  }

  // Fallback: subdomain-based switching (for local dev / other domains)
  const prefix = site === 'GLOBAL' ? 'global' : 'cn';
  return `${prefix}.${domain}${port}`;
};

export const switchSite = (site: SiteMode): void => {
  const targetHost = buildTargetHost(site);
  const { protocol, pathname, search, hash } = window.location;
  // Use https for production domains, keep current protocol for dev
  const targetProtocol = DOMAIN_MAP[targetHost] ? 'https:' : protocol;
  window.location.href = `${targetProtocol}//${targetHost}${pathname}${search}${hash}`;
};
